/**
 * Runtime Configuration Loader
 *
 * Loads /config.json at app startup. Values here OVERRIDE build-time env vars (VITE_*).
 *
 * Priority (highest to lowest):
 *   1. /config.json (runtime - editable after deployment, no rebuild needed)
 *   2. import.meta.env.VITE_* (build-time)
 *   3. Hardcoded defaults / test values
 *
 * This lets DevOps configure the deployed app (Docker volume mount, K8s ConfigMap, etc.)
 * without rebuilding the bundle. Perfect for when the same build needs different
 * config per environment (staging vs production).
 */

let cachedConfig = null;
let loadPromise = null;

/**
 * Load /config.json and cache it. Called once at app startup.
 * Fails gracefully if file is missing or malformed - app continues with env vars.
 */
export async function loadConfig() {
  if (cachedConfig) return cachedConfig;
  if (loadPromise) return loadPromise;

  loadPromise = fetch('/config.json', { cache: 'no-store' })
    .then(r => (r.ok ? r.json() : null))
    .then(data => {
      cachedConfig = data || {};
      return cachedConfig;
    })
    .catch(() => {
      cachedConfig = {};
      return cachedConfig;
    });

  return loadPromise;
}

/** Synchronous accessor - returns cached config (empty object if not loaded yet) */
export function getConfig() {
  return cachedConfig || {};
}

/* ── Typed accessors with env var fallback ─────────────────────────── */

export function getApiBaseUrl() {
  const config = getConfig();
  return (
    config.apiBaseUrl ||
    import.meta.env.VITE_API_BASE_URL ||
    ''
  );
}

export function getTurnstileSiteKey() {
    
  const config = getConfig();
    const TEST_KEY = '1x00000000000000000000AA'; // always passes - safe for dev
    
  return (
    config.turnstile?.siteKey ||
    import.meta.env.VITE_TURNSTILE_SITE_KEY ||
    TEST_KEY
  );
}

export function isTurnstileEnabled() {
  const config = getConfig();
  // Default to true - can be disabled via config
  return config.turnstile?.enabled !== false;
}

export function isFeatureEnabled(featureName) {
  const config = getConfig();
  return config.features?.[featureName] !== false;
}

/** Session timeout in minutes (total duration before auto-logout) */
export function getSessionTimeoutMinutes() {
  const config = getConfig();
  const fromConfig = config.session?.timeoutMinutes;
  const fromEnv = Number(import.meta.env.VITE_SESSION_TIMEOUT_MINUTES);
  const value = fromConfig ?? (isFinite(fromEnv) && fromEnv > 0 ? fromEnv : 30);

  
  return value;
}

/** Show warning modal N minutes before session expires */
export function getSessionWarningMinutes() {
  const config = getConfig();
  const fromConfig = config.session?.warningMinutes;
  const fromEnv = Number(import.meta.env.VITE_SESSION_WARNING_MINUTES);
  const value = fromConfig ?? (isFinite(fromEnv) && fromEnv > 0 ? fromEnv : 2);

  
  return value;
}
